/**
 * Everything the authentic backend needs off `/baked`: the `PS.PAL` UI ramp,
 * the bake manifest (glyph metrics and sprite-frame rectangles), the screen
 * backgrounds and sprite sheets as decoded images, and one pre-embossed,
 * pre-tinted canvas per (font, colour-triple) so drawing a string is a run of
 * plain `drawImage` calls rather than per-pixel work every frame.
 */

import type { BakeManifest, SpriteFrameMeta, SpriteMeta } from "@red-opus/baker/schema";
import type { FontId, TextStyle } from "./draw-ops.ts";
import { type IndexedPalette, loadPalette, loadPsPalette, paletteRgb } from "./ps-palette.ts";
import { buildFontAtlas, type FontAtlas, fontAtlasPath, readFontMeta, SHELL_FONTS } from "./text-metrics.ts";

export interface ShellAssets {
  readonly palette: IndexedPalette;
  readonly manifest: BakeManifest;
  readonly fonts: ReadonlyMap<FontId, FontAtlas>;
  readonly images: ReadonlyMap<string, CanvasImageSource>;
  readonly sprites: ReadonlyMap<string, readonly SpriteFrameMeta[]>;
  /**
   * Decoded `.PAL.json`s beyond the shell's own UI ramp, keyed by the exact
   * `.PAL` filename a `loadPalette` `TtmCommand` names (e.g. `"B-SALUTE.PAL"`)
   * — what the cutscene stage's index-colour draws (`DRAW FILLED/EMPTY RECT`,
   * `DRAW LINE`) resolve against. Empty unless a caller asks for some via
   * `ShellAssetRequest.palettes`; the intro never does, so it never has one.
   */
  readonly palettes: ReadonlyMap<string, IndexedPalette>;
  /** Cache key is `${font}:${body}:${shadow}:${highlight}` — see `tintedAtlas`. */
  readonly tinted: Map<string, CanvasImageSource>;
}

export function bakedPath(assetId: string): string {
  return `/baked/${assetId}.png`;
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = url;
  await image.decode();
  return image;
}

async function loadManifest(): Promise<BakeManifest> {
  const response = await fetch("/baked/manifest.json");
  if (!response.ok) throw new Error(`shell: /baked/manifest.json → ${response.status} ${response.statusText}`);
  return (await response.json()) as BakeManifest;
}

function isSpriteMeta(meta: unknown): meta is SpriteMeta {
  return typeof meta === "object" && meta !== null && Array.isArray((meta as { frames?: unknown }).frames);
}

function collectSpriteFrames(manifest: BakeManifest, ids: readonly string[]): Map<string, readonly SpriteFrameMeta[]> {
  const frames = new Map<string, readonly SpriteFrameMeta[]>();
  for (const id of ids) {
    const entry = manifest.entries.find((candidate) => candidate.id === id);
    if (entry && isSpriteMeta(entry.meta)) frames.set(id, entry.meta.frames);
  }
  return frames;
}

export interface ShellAssetRequest {
  /** Baked ids without the `.png`, e.g. `screens/MAINMENU.SCR`. */
  readonly images: readonly string[];
  readonly sprites: readonly string[];
  /** `.PAL` filenames (e.g. `"B-SALUTE.PAL"`) to decode into `ShellAssets.palettes`. Optional — most surfaces need none of their own. */
  readonly palettes?: readonly string[];
}

/**
 * The first, small half: palette, manifest and the three font atlases. The
 * start gate needs exactly this much to draw its prompt in the game's own
 * bitmap type, and the manifest is also what names the intro's tick source —
 * so splitting the load here is what lets the gate appear while the artwork
 * and the songs are still arriving behind it.
 *
 * **Memoised for the life of the document.**
 *
 * The base load is the `PS.PAL` ramp, the **1.1 MB** bake manifest and four font
 * atlases — the same bytes for every surface that draws in the shell's type,
 * and until this cache existed each of them refetched and re-decoded the lot.
 * That is now on `/fly`'s critical path (`flight/FlightLoadingScreen.tsx` draws
 * the loading screen in `FONT1`), so a player arriving from the briefing screen
 * must not pay for the manifest twice — see
 * `docs/engineering/fly-boot-timing.md` §5.
 *
 * Sharing one `ShellAssets` between surfaces is safe because `loadShellArtwork`
 * copies rather than mutates: it returns a new object with new `images`,
 * `sprites` and `palettes` maps and leaves the base untouched. The one field
 * that *is* shared by reference is `tinted`, and that is a benefit — an embossed
 * atlas built for one screen is reused by the next instead of being rasterised
 * again.
 *
 * The promise is cached, not the result, so two surfaces mounting together share
 * one fetch; a failure is evicted so the next caller retries.
 */
let sharedFonts: Promise<ShellAssets> | undefined;

export function loadShellFonts(): Promise<ShellAssets> {
  sharedFonts ??= fetchShellFonts();
  const pending = sharedFonts;
  pending.catch(() => {
    if (sharedFonts === pending) sharedFonts = undefined;
  });
  return pending;
}

/**
 * A `ShellAssets` carrying nothing but the colour ramp — enough to paint every
 * `fill` and every bevel, and nothing else.
 *
 * It exists for one caller and one property of `canvas-painter.ts`: a `text` op
 * whose font atlas is missing is **skipped**, not an error. So `/fly`'s loading
 * screen (`flight/FlightLoadingScreen.tsx`) can draw its plate and its bar off
 * `PS.PAL` alone — three kilobytes — and let the words appear when the fonts
 * land behind them, instead of holding a black frame until the bake manifest
 * arrives. On the ordinary path in from the briefing the fonts are already
 * memoised and this stage is never seen.
 */
export function paletteOnlyAssets(palette: IndexedPalette): ShellAssets {
  return {
    palette,
    manifest: { version: 0, entries: [], unbaked: [] },
    fonts: new Map(),
    images: new Map(),
    sprites: new Map(),
    palettes: new Map(),
    tinted: new Map(),
  };
}

async function fetchShellFonts(): Promise<ShellAssets> {
  const [palette, manifest] = await Promise.all([loadPsPalette(), loadManifest()]);
  const decoded = await Promise.all(
    SHELL_FONTS.map(async (font) => [`font:${font}`, await loadImage(fontAtlasPath(font))] as const),
  );
  const fonts = new Map<FontId, FontAtlas>(SHELL_FONTS.map((font) => [font, buildFontAtlas(font, readFontMeta(manifest, font))]));
  return { palette, manifest, fonts, images: new Map(decoded), sprites: new Map(), palettes: new Map(), tinted: new Map() };
}

/** The second half: every screen background and sprite sheet, folded into the fonts already in hand. */
export async function loadShellArtwork(base: ShellAssets, request: ShellAssetRequest): Promise<ShellAssets> {
  const wanted = [...new Set([...request.images, ...request.sprites])];
  const decoded = await Promise.all(
    wanted.map(async (id) => {
      return [id, await loadImage(bakedPath(id))] as const;
    }),
  );
  const images = new Map<string, CanvasImageSource>(base.images);
  for (const [id, image] of decoded) images.set(id, image);
  const paletteIds = [...new Set(request.palettes ?? [])];
  const decodedPalettes = await Promise.all(paletteIds.map(async (id) => [id, await loadPalette(id)] as const));
  const palettes = new Map(base.palettes);
  for (const [id, decodedPalette] of decodedPalettes) palettes.set(id, decodedPalette);
  const sprites = new Map(base.sprites);
  for (const [id, frames] of collectSpriteFrames(base.manifest, request.sprites)) sprites.set(id, frames);
  return { ...base, images, sprites, palettes };
}

export async function loadShellAssets(request: ShellAssetRequest): Promise<ShellAssets> {
  return loadShellArtwork(await loadShellFonts(), request);
}

export function fontAtlasImage(assets: ShellAssets, font: FontId): CanvasImageSource | undefined {
  return assets.images.get(`font:${font}`);
}

function styleKey(font: FontId, style: TextStyle): string {
  return `${font}:${style.body}:${style.shadow ?? -1}:${style.highlight ?? -1}`;
}

/**
 * Builds the embossed, tinted copy of a font atlas. The original's raised
 * text is the glyph unioned with itself one row lower: pixels only in the
 * upper copy take `shadow`, pixels in both take `body`, pixels only in the
 * lower copy take `highlight`. Baking that into an atlas one row taller means
 * a string is drawn with one `drawImage` per glyph and the emboss comes for
 * free — and it is pixel-exact against `main-menu.png` (verified glyph-set
 * equality for every Main Menu label).
 */
export function tintedAtlas(assets: ShellAssets, font: FontId, style: TextStyle): CanvasImageSource | undefined {
  const key = styleKey(font, style);
  const cached = assets.tinted.get(key);
  if (cached) return cached;
  const source = fontAtlasImage(assets, font);
  if (!source) return undefined;

  const width = Math.floor(Number((source as HTMLImageElement).naturalWidth ?? 0));
  const height = Math.floor(Number((source as HTMLImageElement).naturalHeight ?? 0)) + 1;
  const read = document.createElement("canvas");
  read.width = width;
  read.height = height;
  const readContext = read.getContext("2d", { willReadFrequently: true });
  if (!readContext) return undefined;
  readContext.drawImage(source, 0, 0);
  const pixels = readContext.getImageData(0, 0, width, height);
  readContext.putImageData(embossAtlas(pixels, assets, style), 0, 0);
  assets.tinted.set(key, read);
  return read;
}

function embossAtlas(source: ImageData, assets: ShellAssets, style: TextStyle): ImageData {
  const { width, height, data } = source;
  const out = new ImageData(width, height);
  const ink = (x: number, y: number): boolean => y >= 0 && y < height && (data[(y * width + x) * 4 + 3] ?? 0) > 0;
  const bodyRgb = paletteRgb(assets.palette, style.body);
  const shadowRgb = paletteRgb(assets.palette, style.shadow ?? style.body);
  const highlightRgb = paletteRgb(assets.palette, style.highlight ?? style.body);
  const flat = style.shadow === undefined && style.highlight === undefined;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const here = ink(x, y);
      const above = ink(x, y - 1);
      if (!here && !above) continue;
      const rgb = flat ? bodyRgb : here && above ? bodyRgb : here ? shadowRgb : highlightRgb;
      if (flat && !here) continue;
      const offset = (y * width + x) * 4;
      out.data[offset] = rgb[0];
      out.data[offset + 1] = rgb[1];
      out.data[offset + 2] = rgb[2];
      out.data[offset + 3] = 255;
    }
  }
  return out;
}
